
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface PasswordResetFormProps {
  email: string;
  setEmail: (email: string) => void;
  setIsResetMode: (isResetMode: boolean) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const PasswordResetForm = ({ 
  email, 
  setEmail, 
  setIsResetMode, 
  loading, 
  setLoading 
}: PasswordResetFormProps) => {
  
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });
      
      if (error) {
        throw error;
      }
      
      toast.success('Password reset link sent!', {
        duration: 6000,
        description: 'Please check your email inbox and click on the link to reset your password.'
      });
      setIsResetMode(false);
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast.error('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 space-y-4">
      <h3 className="text-lg font-medium text-center">Reset Password</h3>
      <p className="text-sm text-gray-500 text-center">
        Enter your email address and we'll send you a link to reset your password.
      </p>
      
      <form onSubmit={handlePasswordReset} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="reset-email">Email</Label>
          <Input 
            id="reset-email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            type="email" 
            placeholder="your@email.com"
            required
          />
        </div>
        
        <div className="flex gap-2">
          <Button 
            type="button" 
            variant="outline" 
            className="w-full"
            onClick={() => setIsResetMode(false)}
          >
            Cancel
          </Button>
          <Button type="submit" className="w-full bg-mint text-forest hover:bg-mint/90" disabled={loading}>
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PasswordResetForm;
